/* global React */

// Path-based AR localization for the component's hardcoded UI labels (project
// content itself comes from content/projects_ar.js). EN strings are unchanged.
function dzIsAr() { return typeof window !== 'undefined' && window.location.pathname.startsWith('/ar/'); }
function dzT(en, ar) { return dzIsAr() ? ar : en; }
function dzProjHref(slug) { return (dzIsAr() ? '/ar' : '') + '/projects/' + (slug ? slug + '/' : ''); }

// Project image URL → optional WebP source URL.
function dzWebp(jpgUrl) {
  if (!jpgUrl) return null;
  var stem = jpgUrl.replace(/^.*\//, '').replace(/\.[^.]+$/, '');
  var list = (typeof window !== 'undefined' && window.DZ_WEBP_AVAILABLE) || [];
  return list.indexOf(stem) !== -1 ? jpgUrl.replace(/\.jpg$/i, '.webp') : null;
}

// Project image URL → optional AVIF source URL (smallest format).
function dzAvif(jpgUrl) {
  if (!jpgUrl) return null;
  var stem = jpgUrl.replace(/^.*\//, '').replace(/\.[^.]+$/, '');
  var list = (typeof window !== 'undefined' && window.DZ_AVIF_AVAILABLE) || [];
  return list.indexOf(stem) !== -1 ? jpgUrl.replace(/\.jpg$/i, '.avif') : null;
}
function dzAvifMobile(jpgUrl) {
  var desktop = dzAvif(jpgUrl);
  return desktop ? desktop.replace(/\.avif$/i, '-mobile.avif') : null;
}

// Project image URL → {w, h} so <img> can declare width/height
// attributes (prevents CLS).
function dzDims(jpgUrl) {
  if (!jpgUrl) return { w: 1600, h: 1000 };
  var stem = jpgUrl.replace(/^.*\//, '').replace(/\.[^.]+$/, '');
  var dims = (typeof window !== 'undefined' && window.DZ_IMAGE_DIMS) || {};
  return dims[stem] || { w: 1600, h: 1000 };
}

// Image-SERP-friendly alt — combines name + category + location +
// optional `imgAltTags`. See same helper in ProjectsPage.jsx.
function dzAlt(item) {
  if (!item) return '';
  var locParts = [];
  if (item.location) locParts.push(item.location);
  if (item.country) locParts.push(item.country);
  var parts = [
    item.name,
    item.categoryLabel,
    locParts.join(', ')
  ];
  if (Array.isArray(item.imgAltTags) && item.imgAltTags.length) {
    parts.push(item.imgAltTags.join(', '));
  }
  return parts.filter(Boolean).join(' — ');
}
/* ──────────────────────────────────────────────────────────────────────
   ProjectDetailPage — /projects/<slug>/
   One JSX, slug-driven. Reads window.location.pathname to determine
   which project's data to render from window.DZ_PROJECTS.items.

   Interactive layers (the "impress me" mandate):
     ▸ Reading progress bar (fixed top, fills as the page scrolls)
     ▸ Cinematic parallax hero (image translates 30% of scroll distance)
     ▸ Per-section reveal on scroll via IntersectionObserver
     ▸ Numbered scope bullets with translateX entrance
     ▸ Stagger reveal of key facts (per-cell delay 80ms)
     ▸ Floating CTA that fades in after the first viewport
     ▸ Dark related-projects strip at the bottom
   All animations respect prefers-reduced-motion.
   ────────────────────────────────────────────────────────────────────── */

const { useState: useDtlState, useEffect: useDtlEffect, useRef: useDtlRef } = React;

/* ─── Reading progress bar ─── */
function ReadingProgress() {
  const [progress, setProgress] = useDtlState(0);

  useDtlEffect(() => {
    let frame = null;
    const onScroll = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const scrolled = window.scrollY;
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const pct = max > 0 ? Math.min(100, (scrolled / max) * 100) : 0;
        setProgress(pct);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div className="dz-detail__progress" aria-hidden="true">
      <div className="dz-detail__progress-bar" style={{ width: progress + '%' }} />
    </div>
  );
}

/* ─── Floating CTA — appears after first viewport ─── */
function FloatingCta() {
  const [visible, setVisible] = useDtlState(false);

  useDtlEffect(() => {
    let frame = null;
    const onScroll = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setVisible(window.scrollY > window.innerHeight * 0.7);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <a
      href="#inquiry"
      className={'dz-detail__floating-cta' + (visible ? ' is-visible' : '')}
      aria-label={dzT('Discuss a similar project', 'ناقش مشروعاً مماثلاً')}
    >
      <span>{dzT('Discuss a similar project', 'ناقش مشروعاً مماثلاً')}</span>
      <span aria-hidden="true">→</span>
    </a>
  );
}

/* ─── Reveal-on-scroll wrapper ─── */
function RevealOnScroll({ children, className }) {
  const [visible, setVisible] = useDtlState(false);
  const ref = useDtlRef(null);

  useDtlEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        obs.disconnect();
      }
    }, { threshold: 0.15, rootMargin: '0px 0px -60px 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className={(className || '') + ' dz-detail__reveal' + (visible ? ' is-visible' : '')}
    >
      {children}
    </section>
  );
}

/* ─── Cinematic parallax hero ─── */
function ParallaxHero({ project }) {
  const [scrollY, setScrollY] = useDtlState(0);

  useDtlEffect(() => {
    let frame = null;
    const onScroll = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        // Only track scroll up to one viewport — beyond that, the hero is offscreen anyway
        const y = Math.min(window.innerHeight, window.scrollY);
        setScrollY(y);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const imageStyle = {
    transform: 'translate3d(0,' + (scrollY * 0.3) + 'px, 0) scale(' + (1 + Math.min(0.06, scrollY * 0.0002)) + ')'
  };

  return (
    <div className="dz-detail__hero">
      <div className="dz-detail__hero-image-wrap" style={imageStyle}>
        <picture>
          {dzAvifMobile(project.image) && <source media="(max-width: 800px)" srcSet={dzAvifMobile(project.image)} type="image/avif" />}
          {dzAvif(project.image) && <source srcSet={dzAvif(project.image)} type="image/avif" />}
          {dzWebp(project.image) && <source srcSet={dzWebp(project.image)} type="image/webp" />}
          <img src={project.image} alt={dzAlt(project)} loading="eager" decoding="async"
               width={dzDims(project.image).w} height={dzDims(project.image).h} />
        </picture>
      </div>

      <div className="dz-detail__hero-content">
        <a href={dzProjHref()} className="dz-detail__back">
          <span aria-hidden="true">←</span>
          <span>{dzT('All projects', 'كل المشاريع')}</span>
        </a>

        <div className="dz-detail__hero-text">
          {project.categoryLabel ? (
            <span className="dz-detail__hero-cat">
              {project.categoryLabel}{project.year ? ' · ' + project.year : ''}
            </span>
          ) : null}

          <h1 className="dz-detail__hero-title">{project.name}</h1>

          <p className="dz-detail__hero-meta">
            {project.location}{project.country ? ', ' + project.country : ''}
            {project.client ? <><span className="dz-detail__sep">·</span>{project.client}</> : null}
          </p>

          {project.tags && project.tags.length ? (
            <div className="dz-detail__hero-tags">
              {project.tags.map((t) => (
                <span key={t} className="dz-detail__chip">{t}</span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ─── Helper: pretty category label ─── */
function categoryDisplay(catId) {
  if (catId === 'bim') return dzT('BIM Projects', 'مشاريع BIM');
  if (catId === 'concept') return dzT('Concept Design', 'التصميم المفاهيمي');
  if (catId === 'interior') return dzT('Interior Design', 'التصميم الداخلي');
  if (catId === 'vr') return dzT('VR & Immersive', 'الواقع الافتراضي والوسائط الغامرة');
  return dzT('Projects', 'المشاريع');
}

/* ─── Page ─── */
/* ── LiteYouTube ───────────────────────────────────────────────────────
   Facade for the testimonial YouTube embed. The raw `<iframe loading="lazy">`
   it replaces still pulled ~600 KB of YouTube scripts that ran on KSP
   (May 17 PSI: TBT 1,260 ms, TTI 11.6 s). This facade renders only the
   thumbnail + play button until the visitor actually clicks — on click,
   we hot-swap to the real iframe with autoplay. Visual output is
   indistinguishable from YouTube's own paused-state poster.

   Thumbnail picks `maxres` if the video has one, otherwise `hq`. AVIF
   isn't available from YouTube — we just trust their JPG sizing. */
function LiteYouTube({ videoId, title }) {
  const [activated, setActivated] = useDtlState(false);
  if (activated) {
    return (
      <iframe
        className="dz-detail__testimonial-video"
        src={'https://www.youtube-nocookie.com/embed/' + videoId + '?rel=0&autoplay=1'}
        title={title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  }
  return (
    <button
      type="button"
      className="dz-detail__testimonial-video dz-detail__lite-yt"
      aria-label={'Play video — ' + title}
      onClick={() => setActivated(true)}
      style={{
        backgroundImage:
          'linear-gradient(rgba(0,0,0,0.05), rgba(0,0,0,0.05)),' +
          'url(https://i.ytimg.com/vi/' + videoId + '/maxresdefault.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#000',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <span aria-hidden="true" style={{
        width: 68, height: 48, borderRadius: 8,
        background: 'rgba(0,0,0,0.6)', display: 'grid', placeItems: 'center',
        transition: 'background .15s ease',
      }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
          <path d="M8 5v14l11-7z"/>
        </svg>
      </span>
    </button>
  );
}

function ProjectDetailPage() {
  const items = (typeof window !== 'undefined' && window.DZ_PROJECTS && window.DZ_PROJECTS.items) || [];

  // Extract slug from URL — supports /projects/<slug>/ and /projects/<slug>
  const path = (typeof window !== 'undefined') ? window.location.pathname : '';
  const match = path.match(/\/projects\/([^/]+)\/?$/);
  const slug = match ? match[1] : '';

  const project = items.find((i) => i.slug === slug);

  if (!project) {
    return (
      <main className="dz-detail dz-detail--missing">
        <div className="dz-detail__missing-inner">
          <span className="dz-detail__missing-eyebrow">404</span>
          <h1 className="dz-detail__missing-title">{dzT('Project not found', 'المشروع غير موجود')}</h1>
          <p className="dz-detail__missing-sub">
            {dzT('This project does not exist or has not been added yet.', 'هذا المشروع غير موجود أو لم يُضَف بعد.')}
          </p>
          <a href={dzProjHref()} className="dz-detail__missing-back">
            <span aria-hidden="true">←</span>
            <span>{dzT('Back to all projects', 'العودة إلى كل المشاريع')}</span>
          </a>
        </div>
      </main>
    );
  }

  const related = items
    .filter((i) => i.slug !== project.slug && i.category === project.category)
    .slice(0, 3);

  return (
    <main className="dz-detail" id="dz-main" aria-labelledby="detail-title">
      <ReadingProgress />

      <ParallaxHero project={project} />

      <div className="dz-detail__inner">

        {/* ── Story narrative ── */}
        {(project.storyParas && project.storyParas.length) || project.description ? (
          <RevealOnScroll className="dz-detail__story">
            <h2 className="dz-detail__section-title" id="detail-title">{dzT('The story', 'القصّة')}</h2>
            {project.storyParas && project.storyParas.length ? (
              project.storyParas.map((para, i) => (
                <p key={i} className="dz-detail__story-para">{para}</p>
              ))
            ) : (
              <p className="dz-detail__story-para">{project.description}</p>
            )}
          </RevealOnScroll>
        ) : null}

        {/* ── Key facts grid ── */}
        {project.keyFacts && project.keyFacts.length ? (
          <RevealOnScroll className="dz-detail__facts">
            <h2 className="dz-detail__section-title">{dzT('Key facts', 'حقائق أساسية')}</h2>
            <ul className="dz-detail__facts-grid">
              {project.keyFacts.map((f, i) => (
                <li
                  key={i}
                  className="dz-detail__fact"
                  style={{ transitionDelay: (i * 80) + 'ms' }}
                >
                  <span className="dz-detail__fact-label">{f.label}</span>
                  <span className="dz-detail__fact-value">{f.value}</span>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        ) : null}

        {/* ── Scope of work ── */}
        {project.scopeBullets && project.scopeBullets.length ? (
          <RevealOnScroll className="dz-detail__scope">
            <h2 className="dz-detail__section-title">{dzT('Scope of work', 'نطاق العمل')}</h2>
            <ol className="dz-detail__scope-list">
              {project.scopeBullets.map((b, i) => (
                <li
                  key={i}
                  className="dz-detail__scope-item"
                  style={{ transitionDelay: (i * 100) + 'ms' }}
                >
                  <span className="dz-detail__scope-num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="dz-detail__scope-text">{b}</span>
                </li>
              ))}
            </ol>
          </RevealOnScroll>
        ) : null}

        {/* ── Partner testimonial (YouTube embed + pull quote) ── */}
        {project.testimonial && project.testimonial.videoId ? (
          <RevealOnScroll className="dz-detail__testimonial">
            <span className="dz-detail__testimonial-eyebrow">
              {project.testimonial.eyebrow || dzT('Trusted by', 'بثقة من')}
            </span>
            <h2
              className="dz-detail__section-title dz-detail__testimonial-heading"
              dangerouslySetInnerHTML={{ __html: project.testimonial.heading || dzT('What our partner says.', 'بكلمات شريكنا.') }}
            />
            <div className="dz-detail__testimonial-grid">
              <div className="dz-detail__testimonial-video-wrap">
                <LiteYouTube
                  videoId={project.testimonial.videoId}
                  title={project.testimonial.videoTitle || dzT('Project testimonial', 'شهادة المشروع')}
                />
              </div>
              <figure className="dz-detail__testimonial-quote">
                <svg className="dz-detail__testimonial-mark" viewBox="0 0 36 28" fill="currentColor" aria-hidden="true">
                  <path d="M0 28V16C0 7 5 1 14 0v6c-4 1-6 4-6 8h6v14H0zm22 0V16C22 7 27 1 36 0v6c-4 1-6 4-6 8h6v14H22z"/>
                </svg>
                <blockquote className="dz-detail__testimonial-text">
                  {project.testimonial.pullQuote}
                </blockquote>
                <figcaption className="dz-detail__testimonial-attribution">
                  {project.testimonial.speakerName ? (
                    <span className="dz-detail__testimonial-name">{project.testimonial.speakerName}</span>
                  ) : null}
                  <span className="dz-detail__testimonial-role">{project.testimonial.speakerRole}</span>
                  <span className="dz-detail__testimonial-company">{project.testimonial.speakerCompany}</span>
                  {project.testimonial.speakerNote ? (
                    <span className="dz-detail__testimonial-note">{project.testimonial.speakerNote}</span>
                  ) : null}
                </figcaption>
              </figure>
            </div>
          </RevealOnScroll>
        ) : null}

        {/* ── Software stack ── */}
        {project.software && project.software.length ? (
          <RevealOnScroll className="dz-detail__software">
            <h2 className="dz-detail__section-title">{dzT('Software stack', 'حزمة البرامج')}</h2>
            <div className="dz-detail__software-chips">
              {project.software.map((s) => (
                <span key={s} className="dz-detail__chip dz-detail__chip--soft">{s}</span>
              ))}
            </div>
          </RevealOnScroll>
        ) : null}

        <p className="dz-detail__cta-link">
          {dzT('Planning a project of this calibre? ', 'تخطّط لمشروع بهذا المستوى؟ ')}
          <a href="#inquiry">{dzT('Discuss your project', 'ناقش مشروعك')}</a>
          {dzT(' and a senior BIM lead replies within 24 hours.', ' وسيرد عليك أحد كبار مختصّي BIM خلال 24 ساعة.')}
        </p>

      </div>

      {/* ── Related projects (dark band) ── */}
      {related.length > 0 ? (
        <section className="dz-detail__related">
          <div className="dz-detail__related-inner">
            <header className="dz-detail__related-head">
              <span className="dz-detail__related-eyebrow">{dzT('Continue exploring', 'واصل الاستكشاف')}</span>
              <h2 className="dz-detail__related-title">
                {dzT('More work in', 'المزيد من الأعمال في')} <em>{categoryDisplay(project.category)}</em>.
              </h2>
            </header>

            <div className="dz-detail__related-grid">
              {related.map((r, i) => (
                <a
                  key={r.slug}
                  href={r.hasDetailPage ? dzProjHref(r.slug) : '#' + r.slug}
                  className="dz-detail__related-card"
                  style={{ transitionDelay: (i * 80) + 'ms' }}
                >
                  <div className="dz-detail__related-image">
                    <picture>
                      {dzAvifMobile(r.image) && <source media="(max-width: 800px)" srcSet={dzAvifMobile(r.image)} type="image/avif" />}
                      {dzAvif(r.image) && <source srcSet={dzAvif(r.image)} type="image/avif" />}
                      {dzWebp(r.image) && <source srcSet={dzWebp(r.image)} type="image/webp" />}
                      <img src={r.image} alt={dzAlt(r)} loading="lazy" decoding="async"
                           width={dzDims(r.image).w} height={dzDims(r.image).h} />
                    </picture>
                  </div>
                  <div className="dz-detail__related-body">
                    <h3 className="dz-detail__related-name">{r.name}</h3>
                    <p className="dz-detail__related-meta">
                      {r.location}{r.country ? ', ' + r.country : ''}
                    </p>
                  </div>
                </a>
              ))}
            </div>

            <a href={dzProjHref()} className="dz-detail__all-link">
              <span>{dzT('See all 20 projects', 'شاهد المشاريع العشرين')}</span>
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </section>
      ) : null}

      <FloatingCta />
    </main>
  );
}

window.ProjectDetailPage = ProjectDetailPage;
